import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  FileBarChart,
  ChartBar,
  DollarSign,
  Users
} from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

// PDF generation
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Excel generation
import * as XLSX from 'xlsx';
import LoadingSpinner from '../../components/LoadingSpinner';

const Reports = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('campaign-performance');
  const [timeframe, setTimeframe] = useState('last-3-months');
  const [campaigns, setCampaigns] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [chartData, setChartData] = useState({});

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  useEffect(() => {
    if (campaigns.length > 0 || influencers.length > 0 || payments.length > 0) {
      generateReportData();
    }
  }, [reportType, timeframe, campaigns, influencers, payments]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch campaigns
      const campaignsRef = collection(db, 'campaigns');
      const campaignsQuery = query(campaignsRef, where('brandId', '==', currentUser.uid));
      const campaignsSnapshot = await getDocs(campaignsQuery);
      const campaignsData = campaignsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCampaigns(campaignsData);

      // Fetch influencers who have applied to brand's campaigns
      const applicationsRef = collection(db, 'applications');
      const applicationsQuery = query(applicationsRef, where('brandId', '==', currentUser.uid));
      const applicationsSnapshot = await getDocs(applicationsQuery);
      
      const influencerIds = [...new Set(applicationsSnapshot.docs.map(doc => doc.data().influencerId))];
      const influencersData = [];
      
      for (const influencerId of influencerIds) {
        const influencerQuery = query(collection(db, 'users'), where('uid', '==', influencerId));
        const influencerSnapshot = await getDocs(influencerQuery);
        if (!influencerSnapshot.empty) {
          influencersData.push({
            id: influencerId,
            ...influencerSnapshot.docs[0].data()
          });
        }
      }
      setInfluencers(influencersData);

      // Fetch payments
      const paymentsRef = collection(db, 'payments');
      const paymentsQuery = query(paymentsRef, where('brandId', '==', currentUser.uid));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      const paymentsData = paymentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPayments(paymentsData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data for reports:', error);
      setLoading(false);
    }
  };

  const generateReportData = () => {
    // Filter data based on selected timeframe
    const { startDate, endDate } = getTimeframeDates(timeframe);
    
    // Generate report data based on report type
    switch (reportType) {
      case 'campaign-performance':
        generateCampaignPerformanceReport(startDate, endDate);
        break;
      case 'influencer-payouts':
        generateInfluencerPayoutsReport(startDate, endDate);
        break;
      case 'spending-analysis':
        generateSpendingAnalysisReport(startDate, endDate);
        break;
      case 'engagement-metrics':
        generateEngagementMetricsReport(startDate, endDate);
        break;
      default:
        setReportData([]);
    }
  };

  const getTimeframeDates = (timeframe) => {
    const now = new Date();
    let startDate, endDate;

    switch (timeframe) {
      case 'last-month':
        startDate = startOfMonth(subMonths(now, 1));
        endDate = endOfMonth(subMonths(now, 1));
        break;
      case 'last-3-months':
        startDate = startOfMonth(subMonths(now, 3));
        endDate = now;
        break;
      case 'last-6-months':
        startDate = startOfMonth(subMonths(now, 6));
        endDate = now;
        break;
      case 'last-year':
        startDate = startOfMonth(subMonths(now, 12));
        endDate = now;
        break;
      default:
        startDate = startOfMonth(subMonths(now, 3));
        endDate = now;
    }

    return { startDate, endDate };
  };

  const generateCampaignPerformanceReport = (startDate, endDate) => {
    // Filter campaigns within the timeframe
    const filteredCampaigns = campaigns.filter(campaign => {
      const campaignStartDate = campaign.startDate?.toDate ? campaign.startDate.toDate() : new Date(campaign.startDate);
      return campaignStartDate >= startDate && campaignStartDate <= endDate;
    });

    // Generate report data
    const data = filteredCampaigns.map(campaign => {
      // Use the campaign's applications field directly instead of calculating
      // This is more accurate as it includes all applicants, not just those with payments
      // We keep this variable for backward compatibility with the code below
      
      // Calculate total spend for this campaign
      const campaignSpend = payments
        .filter(payment => payment.campaignId === campaign.id)
        .reduce((total, payment) => total + (payment.amount || 0), 0);

      // Calculate average engagement rate (mock data for now)
      const engagementRate = Math.random() * 10; // Mock data between 0-10%

      return {
        campaignId: campaign.id,
        campaignName: campaign.name || 'Unnamed Campaign',
        startDate: campaign.startDate?.toDate ? format(campaign.startDate.toDate(), 'MMM dd, yyyy') : 
                  (campaign.startDate ? format(new Date(campaign.startDate), 'MMM dd, yyyy') : 'N/A'),
        endDate: campaign.endDate?.toDate ? format(campaign.endDate.toDate(), 'MMM dd, yyyy') : 
                (campaign.endDate ? format(new Date(campaign.endDate), 'MMM dd, yyyy') : 'N/A'),
        status: campaign.status || 'Unknown',
        influencerCount: campaign.applications || 0,
        budget: campaign.budget || 0,
        spent: campaignSpend,
        engagementRate: engagementRate.toFixed(2) + '%',
        roi: campaignSpend > 0 ? ((engagementRate * 100) / campaignSpend).toFixed(2) : 'N/A'
      };
    });

    setReportData(data);

    // Generate chart data
    const chartLabels = data.map(item => item.campaignName);
    const chartDatasets = [
      {
        label: 'Budget',
        data: data.map(item => item.budget),
        backgroundColor: 'rgba(59, 91, 165, 0.5)'
      },
      {
        label: 'Spent',
        data: data.map(item => item.spent),
        backgroundColor: 'rgba(75, 192, 192, 0.5)'
      }
    ];

    setChartData({
      labels: chartLabels,
      datasets: chartDatasets
    });
  };

  const generateInfluencerPayoutsReport = (startDate, endDate) => {
    // Filter payments within the timeframe
    const filteredPayments = payments.filter(payment => {
      const paymentDate = payment.processedAt?.toDate ? payment.processedAt.toDate() : new Date(payment.processedAt);
      return paymentDate >= startDate && paymentDate <= endDate;
    });

    // Group payments by influencer
    const influencerPayouts = {};
    
    filteredPayments.forEach(payment => {
      if (!influencerPayouts[payment.influencerId]) {
        const influencer = influencers.find(inf => inf.id === payment.influencerId);
        influencerPayouts[payment.influencerId] = {
          influencerId: payment.influencerId,
          influencerName: influencer ? (influencer.displayName || influencer.email || 'Unknown') : 'Unknown',
          totalPaid: 0,
          paymentCount: 0,
          campaigns: new Set(),
          lastPaymentDate: null
        };
      }
      
      influencerPayouts[payment.influencerId].totalPaid += (payment.amount || 0);
      influencerPayouts[payment.influencerId].paymentCount += 1;
      influencerPayouts[payment.influencerId].campaigns.add(payment.campaignId);
      
      const paymentDate = payment.processedAt?.toDate ? payment.processedAt.toDate() : new Date(payment.processedAt);
      if (!influencerPayouts[payment.influencerId].lastPaymentDate || 
          paymentDate > influencerPayouts[payment.influencerId].lastPaymentDate) {
        influencerPayouts[payment.influencerId].lastPaymentDate = paymentDate;
      }
    });

    // Convert to array and format data
    const data = Object.values(influencerPayouts).map(payout => ({
      influencerId: payout.influencerId,
      influencerName: payout.influencerName,
      totalPaid: payout.totalPaid,
      paymentCount: payout.paymentCount,
      campaignCount: payout.campaigns.size,
      averagePayment: payout.totalPaid / payout.paymentCount,
      lastPaymentDate: payout.lastPaymentDate ? format(payout.lastPaymentDate, 'MMM dd, yyyy') : 'N/A'
    }));

    setReportData(data);

    // Generate chart data
    const chartLabels = data.map(item => item.influencerName);
    const chartDatasets = [
      {
        label: 'Total Paid',
        data: data.map(item => item.totalPaid),
        backgroundColor: 'rgba(59, 91, 165, 0.5)'
      }
    ];

    setChartData({
      labels: chartLabels,
      datasets: chartDatasets
    });
  };

  const generateSpendingAnalysisReport = (startDate, endDate) => {
    // Filter payments within the timeframe
    const filteredPayments = payments.filter(payment => {
      const paymentDate = payment.processedAt?.toDate ? payment.processedAt.toDate() : new Date(payment.processedAt);
      return paymentDate >= startDate && paymentDate <= endDate;
    });

    // Group payments by month
    const monthlySpending = {};
    
    filteredPayments.forEach(payment => {
      const paymentDate = payment.processedAt?.toDate ? payment.processedAt.toDate() : new Date(payment.processedAt);
      const monthYear = format(paymentDate, 'MMM yyyy');
      
      if (!monthlySpending[monthYear]) {
        monthlySpending[monthYear] = {
          month: monthYear,
          totalSpent: 0,
          paymentCount: 0,
          influencers: new Set(),
          campaigns: new Set()
        };
      }
      
      monthlySpending[monthYear].totalSpent += (payment.amount || 0);
      monthlySpending[monthYear].paymentCount += 1;
      monthlySpending[monthYear].influencers.add(payment.influencerId);
      monthlySpending[monthYear].campaigns.add(payment.campaignId);
    });

    // Convert to array and sort by date
    const data = Object.values(monthlySpending)
      .map(spending => ({
        month: spending.month,
        totalSpent: spending.totalSpent,
        paymentCount: spending.paymentCount,
        influencerCount: spending.influencers.size,
        campaignCount: spending.campaigns.size,
        averagePayment: spending.totalSpent / spending.paymentCount
      }))
      .sort((a, b) => {
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);
        return dateA - dateB;
      });

    setReportData(data);

    // Generate chart data
    const chartLabels = data.map(item => item.month);
    const chartDatasets = [
      {
        label: 'Monthly Spending',
        data: data.map(item => item.totalSpent),
        backgroundColor: 'rgba(59, 91, 165, 0.5)',
        borderColor: 'rgba(59, 91, 165, 1)',
        borderWidth: 1
      }
    ];

    setChartData({
      labels: chartLabels,
      datasets: chartDatasets
    });
  };

  const generateEngagementMetricsReport = (startDate, endDate) => {
    // For this report, we would need actual engagement data from social media posts
    // Since we don't have that data, we'll create mock data based on campaigns
    
    // Filter campaigns within the timeframe
    const filteredCampaigns = campaigns.filter(campaign => {
      const campaignStartDate = campaign.startDate?.toDate ? campaign.startDate.toDate() : new Date(campaign.startDate);
      return campaignStartDate >= startDate && campaignStartDate <= endDate;
    });

    // Generate mock engagement data for each campaign
    const data = filteredCampaigns.map(campaign => {
      // Mock engagement metrics
      const likes = Math.floor(Math.random() * 10000);
      const comments = Math.floor(Math.random() * 1000);
      const shares = Math.floor(Math.random() * 500);
      const clicks = Math.floor(Math.random() * 2000);
      const impressions = Math.floor(Math.random() * 50000);
      
      return {
        campaignId: campaign.id,
        campaignName: campaign.name || 'Unnamed Campaign',
        likes,
        comments,
        shares,
        clicks,
        impressions,
        engagementRate: ((likes + comments + shares) / impressions * 100).toFixed(2) + '%',
        clickThroughRate: (clicks / impressions * 100).toFixed(2) + '%',
        costPerEngagement: payments
          .filter(payment => payment.campaignId === campaign.id)
          .reduce((total, payment) => total + (payment.amount || 0), 0) / (likes + comments + shares)
      };
    });

    setReportData(data);

    // Generate chart data
    const chartLabels = data.map(item => item.campaignName);
    const chartDatasets = [
      {
        label: 'Likes',
        data: data.map(item => item.likes),
        backgroundColor: 'rgba(59, 91, 165, 0.5)'
      },
      {
        label: 'Comments',
        data: data.map(item => item.comments),
        backgroundColor: 'rgba(75, 192, 192, 0.5)'
      },
      {
        label: 'Shares',
        data: data.map(item => item.shares),
        backgroundColor: 'rgba(255, 159, 64, 0.5)'
      }
    ];

    setChartData({
      labels: chartLabels,
      datasets: chartDatasets
    });
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    const reportTitle = getReportTitle();
    
    // Add title
    doc.setFontSize(18);
    doc.text(reportTitle, 14, 22);
    
    // Add timeframe
    doc.setFontSize(12);
    doc.text(`Timeframe: ${getTimeframeLabel(timeframe)}`, 14, 32);
    
    // Add date generated
    doc.setFontSize(10);
    doc.text(`Generated on: ${format(new Date(), 'MMM dd, yyyy, HH:mm')}`, 14, 38);
    
    // Add table
    const tableColumn = getReportColumns();
    const tableRows = reportData.map(item => Object.values(getReportRowData(item)));
    
    doc.autoTable({
      startY: 45,
      head: [tableColumn],
      body: tableRows,
      theme: 'striped',
      headStyles: { fillColor: [59, 91, 165] }
    });
    
    // Save the PDF
    doc.save(`${reportType}-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  const downloadExcel = () => {
    const reportTitle = getReportTitle();
    const worksheet = XLSX.utils.json_to_sheet(reportData.map(item => getReportRowData(item)));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, reportTitle);
    XLSX.writeFile(workbook, `${reportType}-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const getReportTitle = () => {
    switch (reportType) {
      case 'campaign-performance':
        return 'Campaign Performance Report';
      case 'influencer-payouts':
        return 'Influencer Payouts Report';
      case 'spending-analysis':
        return 'Spending Analysis Report';
      case 'engagement-metrics':
        return 'Engagement Metrics Report';
      default:
        return 'Report';
    }
  };

  const getTimeframeLabel = (timeframe) => {
    switch (timeframe) {
      case 'last-month':
        return 'Last Month';
      case 'last-3-months':
        return 'Last 3 Months';
      case 'last-6-months':
        return 'Last 6 Months';
      case 'last-year':
        return 'Last Year';
      default:
        return 'Last 3 Months';
    }
  };

  const getReportColumns = () => {
    switch (reportType) {
      case 'campaign-performance':
        return ['Campaign', 'Start Date', 'End Date', 'Status', 'Influencers', 'Budget', 'Spent', 'Engagement', 'ROI'];
      case 'influencer-payouts':
        return ['Influencer', 'Total Paid', 'Payments', 'Campaigns', 'Avg. Payment', 'Last Payment'];
      case 'spending-analysis':
        return ['Month', 'Total Spent', 'Payments', 'Influencers', 'Campaigns', 'Avg. Payment'];
      case 'engagement-metrics':
        return ['Campaign', 'Likes', 'Comments', 'Shares', 'Clicks', 'Impressions', 'Engagement Rate', 'CTR', 'CPE'];
      default:
        return [];
    }
  };

  const getReportRowData = (item) => {
    switch (reportType) {
      case 'campaign-performance':
        return {
          campaign: item.campaignName,
          startDate: item.startDate,
          endDate: item.endDate,
          status: item.status,
          influencers: item.influencerCount,
          budget: formatCurrency(item.budget),
          spent: formatCurrency(item.spent),
          engagement: item.engagementRate,
          roi: item.roi
        };
      case 'influencer-payouts':
        return {
          influencer: item.influencerName,
          totalPaid: formatCurrency(item.totalPaid),
          payments: item.paymentCount,
          campaigns: item.campaignCount,
          avgPayment: formatCurrency(item.averagePayment),
          lastPayment: item.lastPaymentDate
        };
      case 'spending-analysis':
        return {
          month: item.month,
          totalSpent: formatCurrency(item.totalSpent),
          payments: item.paymentCount,
          influencers: item.influencerCount,
          campaigns: item.campaignCount,
          avgPayment: formatCurrency(item.averagePayment)
        };
      case 'engagement-metrics':
        return {
          campaign: item.campaignName,
          likes: item.likes.toLocaleString(),
          comments: item.comments.toLocaleString(),
          shares: item.shares.toLocaleString(),
          clicks: item.clicks.toLocaleString(),
          impressions: item.impressions.toLocaleString(),
          engagementRate: item.engagementRate,
          ctr: item.clickThroughRate,
          cpe: formatCurrency(item.costPerEngagement)
        };
      default:
        return {};
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const renderReportTable = () => {
    if (reportData.length === 0) {
      return (
        <div className="text-center py-10">
          <p className="text-sm text-gray-500">No data available for the selected timeframe.</p>
        </div>
      );
    }

    const columns = getReportColumns();

    return (
      <div className="mt-6 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {reportData.map((item, index) => {
              const rowData = getReportRowData(item);
              return (
                <tr key={index}>
                  {Object.values(rowData).map((value, cellIndex) => (
                    <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {value}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderReportCard = (title, icon, description, reportType, currentReportType, onClick) => {
    const isActive = reportType === currentReportType;
    const Icon = icon;

    return (
      <div
        onClick={() => onClick(reportType)}
        className={`${
          isActive
            ? 'bg-primary bg-opacity-10 border-primary'
            : 'bg-white hover:bg-gray-50 border-gray-200'
        } cursor-pointer border rounded-lg p-4 transition-all duration-200`}
      >
        <div className="flex items-center">
          <div className={`${isActive ? 'bg-primary' : 'bg-gray-100'} p-2 rounded-md`}>
            <Icon className={`h-6 w-6 ${isActive ? 'text-white' : 'text-gray-500'}`} />
          </div>
          <div className="ml-4">
            <h3 className={`text-sm font-medium ${isActive ? 'text-primary' : 'text-gray-900'}`}>{title}</h3>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Reports</h2>
        <p className="text-sm text-gray-600">Generate and download comprehensive reports for your campaigns and influencers</p>
      </div>

      <div className="bg-white shadow-sm rounded-lg">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {renderReportCard(
              'Campaign Performance',
              ChartBar,
              'Analyze campaign metrics and ROI',
              'campaign-performance',
              reportType,
              setReportType
            )}
            {renderReportCard(
              'Influencer Payouts',
              DollarSign,
              'Track payments to influencers',
              'influencer-payouts',
              reportType,
              setReportType
            )}
            {renderReportCard(
              'Spending Analysis',
              FileBarChart,
              'Analyze your spending over time',
              'spending-analysis',
              reportType,
              setReportType
            )}
            {renderReportCard(
              'Engagement Metrics',
              Users,
              'Measure audience engagement',
              'engagement-metrics',
              reportType,
              setReportType
            )}
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div className="flex space-x-2">
              <button
                onClick={() => setTimeframe('last-month')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${timeframe === 'last-month' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Last Month
              </button>
              <button
                onClick={() => setTimeframe('last-3-months')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${timeframe === 'last-3-months' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Last 3 Months
              </button>
              <button
                onClick={() => setTimeframe('last-6-months')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${timeframe === 'last-6-months' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Last 6 Months
              </button>
              <button
                onClick={() => setTimeframe('last-year')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${timeframe === 'last-year' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Last Year
              </button>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={downloadPDF}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Download PDF
              </button>
              <button
                onClick={downloadExcel}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Download Excel
              </button>
            </div>
          </div>

          {loading ? (
            null
          ) : (
            <>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-2">{getReportTitle()}</h3>
                <p className="text-sm text-gray-500">
                  Timeframe: {getTimeframeLabel(timeframe)} | Generated on: {format(new Date(), 'MMM dd, yyyy')}
                </p>
              </div>

              {renderReportTable()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
